import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, InputNumber, Row, Select, Table, Tooltip } from 'antd';
import { CalendarOutlined, EyeOutlined } from '@ant-design/icons';
import ReportPageLayout from './ReportPageLayout';
import ReportFiltersCard from './ReportFiltersCard';
import LeaveQuotaDetailModal from './LeaveQuotaDetailModal';
import { reportsApi } from '../../utils/api';
import { extractRows } from './reportUtils';
import { TableSkeleton } from '../../components/SkeletonLoaders';

const LeaveQuotaReport = () => {
  const [filters, setFilters] = useState({ month: 1, year: 1405, employee_id: 'all' });
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.getLeaveQuotaReport(filters);
      setRows(extractRows(res));
      setEmployees(res?.filters?.employees || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 350);
    return () => clearTimeout(timer);
  }, [filters]);

  const monthOptions = useMemo(
    () => [
      { value: 1, label: 'فروردین' },
    { value: 2, label: 'اردیبهشت' },
    { value: 3, label: 'خرداد' },
    { value: 4, label: 'تیر' },
    { value: 5, label: 'مرداد' },
    { value: 6, label: 'شهریور' },
    { value: 7, label: 'مهر' },
    { value: 8, label: 'آبان' },
    { value: 9, label: 'آذر' },
    { value: 10, label: 'دی' },
    { value: 11, label: 'بهمن' },
      { value: 12, label: 'اسفند' },
    ],
    [],
  );

  const monthLabel = monthOptions.find((m) => m.value === filters.month)?.label ?? '';

  const openDetailModal = (row) => {
    const employeeId = row?.employee_id ?? row?.id;
    if (employeeId == null || employeeId === '') return;
    setDetailTarget({
      employeeId,
      employeeName: row?.employee_name || row?.name || '',
      rowSnapshot: row,
    });
    setDetailModalOpen(true);
  };

  return (
    <ReportPageLayout
      headerIcon={<CalendarOutlined />}
      title="سهمیه مرخصی کارکنان"
      subtitle="بررسی سهمیه هر کارمند در ماه"
    >
      <ReportFiltersCard>
        <Form layout="vertical" className="custom-font">
          <Row gutter={[16, 12]}>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="ماه" className="mb-0">
                <Select value={filters.month} onChange={(v) => setFilters((p) => ({ ...p, month: v || 1 }))} options={monthOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Form.Item label="سال (شمسی)" className="mb-0">
                <InputNumber min={1300} max={1600} value={filters.year} onChange={(v) => setFilters((p) => ({ ...p, year: v || 1405 }))} className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8} lg={12}>
              <Form.Item label="کارمند" className="mb-0">
                <Select
                  value={filters.employee_id}
                  onChange={(v) => setFilters((p) => ({ ...p, employee_id: v }))}
                  options={[{ value: 'all', label: 'همه' }, ...employees.map((e) => ({ value: e.id, label: e.name }))]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </ReportFiltersCard>
      {loading ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <Table
          rowKey={(r, i) => r.id || i}
          dataSource={rows}
          scroll={{ x: 900 }}
          className="custom-font"
          columns={[
            { title: 'کارمند', render: (_, r) => r?.employee_name || r?.name || '-' },
            { title: 'مرخصی استحقاقی', render: (_, r) => r?.allocated || r?.total || '-' },
            { title: 'استفاده شده', render: (_, r) => r?.used || '-' },
            { title: 'باقی‌مانده', render: (_, r) => r?.remaining || '-' },
            {
              title: 'جزئیات',
              width: 88,
              align: 'center',
              render: (_, r) => (
                <Tooltip title="مشاهده جزئیات سهمیه">
                  <Button
                    type="text"
                    shape="circle"
                    icon={<EyeOutlined className="text-lg text-indigo-600 dark:text-indigo-400" />}
                    onClick={() => openDetailModal(r)}
                    aria-label="مشاهده جزئیات سهمیه"
                  />
                </Tooltip>
              ),
            },
          ]}
        />
      )}
      <LeaveQuotaDetailModal
        open={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailTarget(null);
        }}
        employeeId={detailTarget?.employeeId}
        year={filters.year}
        month={filters.month}
        monthLabel={monthLabel}
        employeeName={detailTarget?.employeeName}
        rowSnapshot={detailTarget?.rowSnapshot}
      />
    </ReportPageLayout>
  );
};

export default LeaveQuotaReport;
